/**
 * Coach Synthesis Layer
 *
 * Sits ABOVE the analyzers. After all analyzers in a scope have fired and
 * emitted observations, the synthesizer:
 *
 *   1. Co-locates observations that describe the same thing (same setup,
 *      same ATM, same trade, overlapping evidence) — analyzers don't talk
 *      to each other, so this is where related signals get connected.
 *
 *   2. Identifies the trader's CURRENT EDGES (positive observations that
 *      reinforce each other) and CURRENT LEAKS (warnings that compound).
 *
 *   3. Emits a single TOP-OF-FEED observation — a short narrative that
 *      reads like a coach speaking, not a list of stats. The list of
 *      individual observations is still there for drill-down; this just
 *      synthesizes the top-line story.
 *
 * Why this is one layer and not per-scope:
 *   The ranking + clustering logic is identical across scopes. Each scope
 *   calls `synthesize(observations, ctx)` after its analyzers run, and the
 *   synthesis observation gets prepended to the scope's output.
 *
 * Inputs:
 *   observations — Array<Observation> from a scope's analyzers
 *   ctx — { scope: 'setup'|'atm'|'day'|..., scopeId: 'name', tradeCount?: number }
 *
 * Output:
 *   A single Observation with category='process', scope='synthesis',
 *   formatted as "State of <scope>" with a paragraph-style detail.
 *   Returns null when there's nothing meaningful to synthesize (≤1 obs).
 */

import { CATEGORY, SEVERITY, makeObservation } from '../types.js';
import { temporalNarrativeClause } from './temporalContext.js';

const SUCCESS = 'success';
const WARNING = 'warning';
const CRITICAL = 'critical';

/**
 * Cluster observations into edges (positive) and leaks (negative). Within
 * each cluster, sort by priority (highest first) so the synthesizer pulls
 * the most-impactful signal as the headline.
 */
export const clusterObservations = (observations) => {
  if (!Array.isArray(observations) || observations.length === 0) {
    return { edges: [], leaks: [], info: [] };
  }
  const edges = [];
  const leaks = [];
  const info = [];
  for (const o of observations) {
    if (!o) continue;
    if (o.severity === SUCCESS) edges.push(o);
    else if (o.severity === WARNING || o.severity === CRITICAL) leaks.push(o);
    else info.push(o);
  }
  const byPriority = (a, b) => (b.priority || 0) - (a.priority || 0);
  edges.sort(byPriority);
  leaks.sort(byPriority);
  info.sort(byPriority);
  return { edges, leaks, info };
};

/**
 * Strip a trailing period if present — used so we can chain observation
 * titles into a sentence without producing ".." sequences.
 */
const trimDot = (s) => (typeof s === 'string') ? s.replace(/\.\s*$/, '') : '';

const SCOPE_VERB = {
  setup: 'this setup',
  atm:   'this ATM',
  day:   'today',
  session: 'this session',
  trade: 'this trade',
  account: 'this account',
  progress: 'the last 30 days',
};

const scopeNoun = (scope, scopeId) => {
  const verb = SCOPE_VERB[scope] || scope || 'scope';
  if (scopeId && scope !== 'day' && scope !== 'session') return `${verb} ("${scopeId}")`;
  return verb;
};

/**
 * Build the narrative paragraph. Templated for determinism (no LLM
 * required) but composed in a way that reads as a coach summary, not a
 * list of cards.
 *
 * The paragraph follows a fixed shape so the trader knows what to expect:
 *   1. Headline edge OR leak (whichever is the highest-priority signal)
 *   2. Supporting context (the next 1-2 signals in the same direction)
 *   3. Counterpoint (the highest signal in the OPPOSITE direction, if any)
 *   4. Recommended next move (synthesized from the headline's action)
 */
const buildParagraph = (clusters, scope, scopeId) => {
  const { edges, leaks, info } = clusters;
  const noun = scopeNoun(scope, scopeId);
  const sentences = [];

  // 1. Headline — the highest-priority signal regardless of polarity.
  const allRanked = [...edges, ...leaks, ...info]
    .sort((a, b) => (b.priority || 0) - (a.priority || 0));
  if (allRanked.length === 0) return null;
  const headline = allRanked[0];
  const headlineIsEdge = headline.severity === SUCCESS;

  sentences.push(
    headlineIsEdge
      ? `The strongest signal on ${noun} is an edge: ${trimDot(headline.title)}.`
      : `The most-actionable signal on ${noun} is a leak: ${trimDot(headline.title)}.`
  );

  // 2. Supporting context — the next signal of the same polarity, if any.
  const sameSidePool = headlineIsEdge ? edges : leaks;
  const supporting = sameSidePool.find(o => o.id !== headline.id);
  if (supporting) {
    sentences.push(
      headlineIsEdge
        ? `Reinforced by: ${trimDot(supporting.title)}.`
        : `Compounded by: ${trimDot(supporting.title)}.`
    );
  }

  // 3. Counterpoint — the highest opposite-polarity signal, if any.
  const oppositePool = headlineIsEdge ? leaks : edges;
  const counterpoint = oppositePool[0];
  if (counterpoint) {
    sentences.push(
      headlineIsEdge
        ? `Counterweight: ${trimDot(counterpoint.title)}.`
        : `Bright spot: ${trimDot(counterpoint.title)}.`
    );
  }

  // 4. Next move — pull from the headline observation's action.
  if (headline.action) {
    sentences.push(`Next move: ${trimDot(headline.action)}.`);
  }

  return sentences.join(' ');
};

/**
 * Detect cross-domain pattern fusion: when two observations from DIFFERENT
 * categories (e.g., execution + discipline, or setup + confluence) describe
 * overlapping situations, we have higher-order intelligence — call it out
 * explicitly so the trader sees the connection.
 *
 * Returns an extra synthesis sentence to append, or null if no cross-domain
 * link is found.
 */
const detectCrossDomainLink = (clusters) => {
  const all = [...clusters.edges, ...clusters.leaks];
  if (all.length < 2) return null;

  const cats = new Map(); // category → [observations in that category]
  for (const o of all) {
    if (!o.category) continue;
    if (!cats.has(o.category)) cats.set(o.category, []);
    cats.get(o.category).push(o);
  }
  if (cats.size < 2) return null;

  // Pair the two highest-priority observations from different categories
  // and emit a coaching connection.
  const topPerCategory = [];
  for (const [, list] of cats) {
    list.sort((a, b) => (b.priority || 0) - (a.priority || 0));
    topPerCategory.push(list[0]);
  }
  topPerCategory.sort((a, b) => (b.priority || 0) - (a.priority || 0));
  const a = topPerCategory[0];
  const b = topPerCategory[1];
  if (!a || !b) return null;
  if (a.category === b.category) return null;

  return `These two patterns (${a.category} + ${b.category}) likely describe the same trades — the connection between them is where the coaching lives.`;
};

/**
 * Synthesize a top-of-feed observation from a list of analyzer observations.
 *
 * @param {Array<Observation>} observations — output from a scope's analyzers
 * @param {{ scope: string, scopeId?: string, tradeCount?: number }} ctx
 * @returns {Observation|null}
 */
export const synthesize = (observations, ctx = {}) => {
  if (!Array.isArray(observations) || observations.length <= 1) return null;
  const { scope = 'global', scopeId = '' } = ctx;

  const clusters = clusterObservations(observations);
  const total = clusters.edges.length + clusters.leaks.length + clusters.info.length;
  if (total === 0) return null;

  const paragraph = buildParagraph(clusters, scope, scopeId);
  if (!paragraph) return null;

  // Append the cross-domain link if we found one — keeps the synthesis
  // sentence focused on the coach insight, not just a recap.
  const crossLink = detectCrossDomainLink(clusters);
  // Append temporal-narrative clause when observations carry temporal
  // context (i.e., the profile-aware enricher ran upstream). Says things
  // like "2 of these have been firing for 4+ runs — Coach has been
  // watching." Returns null when there's nothing temporal worth saying.
  const allObs = [...clusters.edges, ...clusters.leaks, ...clusters.info];
  const temporalClause = temporalNarrativeClause(allObs);
  const extras = [crossLink, temporalClause].filter(Boolean).join(' ');
  const detail = extras ? `${paragraph} ${extras}` : paragraph;

  // Severity reflects the headline. If the strongest signal is an edge,
  // overall posture is success-leaning; if it's a leak, warning-leaning.
  // Critical leaks bubble up to critical so the synthesis card itself
  // signals urgency without the trader needing to scan the cards below.
  const headline = [...clusters.edges, ...clusters.leaks, ...clusters.info]
    .sort((a, b) => (b.priority || 0) - (a.priority || 0))[0];
  let severity = SEVERITY.INFO;
  if (headline?.severity === CRITICAL) severity = SEVERITY.CRITICAL;
  else if (headline?.severity === WARNING) severity = SEVERITY.WARNING;
  else if (headline?.severity === SUCCESS) severity = SEVERITY.SUCCESS;

  const titlePrefix = scope === 'day' ? 'State of today'
    : scope === 'session' ? 'State of this session'
    : scope === 'progress' ? 'State of the last 30 days'
    : scopeId
      ? `State of "${scopeId}"`
      : 'State of the trader';

  return makeObservation({
    id: `synthesis:${scope}:${scopeId || 'all'}`,
    analyzer: 'synthesis',
    scope,
    scopeId,
    severity,
    category: CATEGORY.PROCESS,
    title: titlePrefix,
    detail,
    action: '',  // The headline observation already carries the action; not duplicating
    evidence: {
      observationCount: total,
      edgeCount: clusters.edges.length,
      leakCount: clusters.leaks.length,
      headlineId: headline?.id || null,
      // Synthesis priority MUST sit above all underlying observations so
      // the renderer surfaces it first. Cap at 100.
      headlinePriority: headline?.priority || 0,
    },
    confidence: 0.75,
    priority: 100,
  });
};

/**
 * Convenience: take a scope's analyzer output, synthesize, and return a
 * single array with the synthesis prepended (when one was produced).
 *
 * Designed to be called inline at the end of a scope's analyzeXXX() right
 * before topObservations() ranks everything.
 */
export const synthesizeAndPrepend = (observations, ctx = {}) => {
  const synth = synthesize(observations, ctx);
  if (!synth) return observations;
  return [synth, ...observations];
};
